import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, DialogContent, Stack } from '@mui/material'
import T from 'T'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDialogActions from 'components/common/HHDialogActions'
import CancelButton from 'components/buttons/CancelButton'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import Dialog from '@mui/material/Dialog'
import { RECURRENCE_V2, EVENT_INSTANCE_TYPES } from 'settings/constants/service'
import { toast } from 'react-toastify'
import { isYearlyRecurrence } from 'utils/service'
import { isAddressObjectEmpty } from 'utils/helper'
import ScheduleTabDraft from 'components/configured-service/CreateDraftConfiguredServiceDialog/ScheduleTabDraft/ScheduleTabDraft'
import {
  CREATE_DRAFT_CS_TABS,
  changeToCreateTab,
  changeToScheduleTab,
  selectCSDCurrentTab,
} from 'slices/configuredService/createDraftCSDSlice'
import CreateDraftServiceForm from 'components/configured-service/CreateDraftConfiguredServiceDialog/CreateDraftServiceForm'
import { Z_INDEX } from 'theme/zIndex'
import { useCreateDraftCSSubmit } from 'components/configured-service/CreateDraftConfiguredServiceDialog/hooks/useCreateDraftCSSubmit'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useFormState, useWatch } from 'react-hook-form'
import {
  findRecurrenceOptionByKey,
  getUniqueRecurrenceOptions,
} from 'components/configured-service/EditServiceRecurrenceDialog/utils/recurrenceOptionsHelper'
import { useCreateDraftValidation } from 'components/configured-service/CreateDraftConfiguredServiceDialog/hooks/useCreateDraftValidation'
import { CreateDraftConfiguredServiceProvider, useCreateDraftConfiguredServiceContext } from './CreateDraftConfiguredServiceContext'

const CreateDraftConfiguredServiceDialogContent = () => {
  const {
    methods,
    metaFiltersData,
    handleRegisterFiltersReset,
    canEditPrice,
    selectedPricedService,
    isSelectedPricedServiceOnRequest,
    accountId,
    resetDialogState,
    isOpen,
    onClose,
    onSuccess,
    onSuccessReturnToAccount,
    renderAsPanel,
    isDesktop,
  } = useCreateDraftConfiguredServiceContext()

  const [isLocalSubmitting, setIsLocalSubmitting] = React.useState(false)
  const isSubmittingRef = React.useRef(false)

  const { control } = methods
  const { isValid } = useFormState({
    control,
  })

  const recurrenceOptions = useMemo(() => getUniqueRecurrenceOptions(metaFiltersData), [metaFiltersData])

  const { handleSubmit, setValue, trigger } = methods

  const { createDraftService, createAndPublishDraftService, isCreating, isPublishing, isUpdating, isComplexSubmitting } =
    useCreateDraftCSSubmit({
      methods,
      canEditPrice,
      isSelectedPricedServiceOnRequest,
      selectedPricedService,
      accountId,
      recurrenceOptions,
    })

  const dispatch = useDispatch()
  const hasSelectedPricedService = useMemo(() => !!selectedPricedService, [selectedPricedService])
  const currentTab = useSelector(selectCSDCurrentTab, shallowEqual)
  const isScheduleTab = useMemo(() => currentTab === CREATE_DRAFT_CS_TABS.SCHEDULE, [currentTab])
  const isCreateTab = useMemo(() => currentTab === CREATE_DRAFT_CS_TABS.CREATE, [currentTab])
  const isNonOnRequestService = useMemo(
    () => hasSelectedPricedService && selectedPricedService?.serviceType !== RECURRENCE_V2.ON_REQUEST,
    [hasSelectedPricedService, selectedPricedService]
  )
  const showBackToServices = useMemo(() => isNonOnRequestService && isScheduleTab, [isNonOnRequestService, isScheduleTab])
  const showScheduleServiceDay = useMemo(() => isNonOnRequestService && isCreateTab, [isNonOnRequestService, isCreateTab])
  const showPublish = useMemo(
    () => hasSelectedPricedService && (isSelectedPricedServiceOnRequest || isScheduleTab),
    [hasSelectedPricedService, isSelectedPricedServiceOnRequest, isScheduleTab]
  )
  const isCreatingOrPublishing = useMemo(
    () => isCreating || isPublishing || isUpdating || isLocalSubmitting || isComplexSubmitting,
    [isCreating, isPublishing, isUpdating, isLocalSubmitting, isComplexSubmitting]
  )
  const formLocation = useWatch({ control, name: 'location' })
  const formLocationHasNoAddress = useMemo(() => isAddressObjectEmpty(formLocation?.address), [formLocation])
  const isActionDisabled = useMemo(
    () => !isValid || isCreatingOrPublishing || formLocationHasNoAddress,
    [isValid, isCreatingOrPublishing, formLocationHasNoAddress]
  )
  const isCreateDraftDisabled = useMemo(
    () => isCreatingOrPublishing || !hasSelectedPricedService || !formLocation || formLocationHasNoAddress,
    [isCreatingOrPublishing, hasSelectedPricedService, formLocation, formLocationHasNoAddress]
  )

  const isScheduleButtonDisabled = useMemo(
    () => !hasSelectedPricedService || isSelectedPricedServiceOnRequest || !formLocation || formLocationHasNoAddress,
    [hasSelectedPricedService, isSelectedPricedServiceOnRequest, formLocation, formLocationHasNoAddress]
  )

  const { validateNonWeeklyFields } = useCreateDraftValidation({
    methods,
    recurrenceOptions,
    isScheduleTab,
    isNonOnRequestService,
  })

  const executePublishFlow = useCallback(
    async onSuccessCallback => {
      if (isSubmittingRef.current) return

      if (!validateNonWeeklyFields()) {
        await trigger(['eventInstanceType', 'eventOccurrence', 'dayOfTheWeek', 'dayOfTheMonth'])
        return
      }

      isSubmittingRef.current = true
      setIsLocalSubmitting(true)
      try {
        setValue('forPublish', true, { shouldValidate: false })
        const isOk = isSelectedPricedServiceOnRequest ? await trigger(['serviceStartDate', 'onRequestAction']) : await trigger()
        if (!isOk) return

        await handleSubmit(async formData => {
          const serviceIntervalObj = findRecurrenceOptionByKey(recurrenceOptions, formData.serviceInterval)
          if (isYearlyRecurrence(serviceIntervalObj?.recurrenceInterval)) {
            const frequency = +formData.recurrenceFrequency || 1
            // eslint-disable-next-line no-param-reassign
            formData.eventOccurrence = Array(frequency).fill(1)
            // eslint-disable-next-line no-param-reassign
            formData.dayOfTheWeek = Array(frequency).fill('Monday')
            // eslint-disable-next-line no-param-reassign
            formData.eventInstanceType = EVENT_INSTANCE_TYPES.DAY_OF_WEEK
          }

          const publishPromise = createAndPublishDraftService(formData)
          toast.promise(publishPromise, {
            pending: {
              render() {
                return T.SAVING_SERVICE
              },
              icon: true,
            },
          })
          try {
            const publishResult = await publishPromise
            if (publishResult) {
              resetDialogState()
              onClose()
              onSuccessCallback(publishResult)
            }
          } catch (error) {
            // error is handled by toast.promise
          }
        })()
      } finally {
        setValue('forPublish', false, { shouldValidate: false })
        isSubmittingRef.current = false
        if (!isPublishing && !isCreating) {
          setIsLocalSubmitting(false)
        }
      }
    },
    [
      validateNonWeeklyFields,
      trigger,
      setValue,
      isSelectedPricedServiceOnRequest,
      handleSubmit,
      recurrenceOptions,
      createAndPublishDraftService,
      resetDialogState,
      onClose,
      isPublishing,
      isCreating,
    ]
  )

  const handlePublishAndReturnToAccountClick = useCallback(
    () => executePublishFlow(() => onSuccessReturnToAccount()),
    [executePublishFlow, onSuccessReturnToAccount]
  )

  const handleGoToScheduleClick = useCallback(() => {
    dispatch(changeToScheduleTab())
  }, [dispatch])

  const handleBackToServicesClick = useCallback(() => {
    dispatch(changeToCreateTab())
  }, [dispatch])

  const handlePublishClick = useCallback(
    () => executePublishFlow(publishResult => onSuccess(publishResult, isScheduleTab ? 'map-routing' : undefined)),
    [executePublishFlow, onSuccess, isScheduleTab]
  )

  const handlePublishNowClick = useCallback(
    () => executePublishFlow(publishResult => onSuccess(publishResult)),
    [executePublishFlow, onSuccess]
  )

  // Clear local submitting when RTK query state changes to true (handover)
  React.useEffect(() => {
    if (isCreating || isPublishing || isUpdating) {
      setIsLocalSubmitting(false)
    }
  }, [isCreating, isPublishing, isUpdating])

  const handleSaveDraftClick = useCallback(async () => {
    if (isSubmittingRef.current) return
    isSubmittingRef.current = true
    setIsLocalSubmitting(true)
    try {
      const formData = methods.getValues()

      const serviceIntervalObj = findRecurrenceOptionByKey(recurrenceOptions, formData.serviceInterval)
      if (isYearlyRecurrence(serviceIntervalObj?.recurrenceInterval)) {
        const frequency = +formData.recurrenceFrequency || 1
        formData.eventOccurrence = Array(frequency).fill(1)
        formData.dayOfTheWeek = Array(frequency).fill('Monday')
        formData.eventInstanceType = EVENT_INSTANCE_TYPES.DAY_OF_WEEK
      }

      const savePromise = createDraftService(formData)
      toast.promise(savePromise, {
        pending: {
          render() {
            return T.CREATING_DRAFT
          },
          icon: true,
        },
      })
      const result = await savePromise
      if (result) {
        resetDialogState()
        onClose()
        onSuccess(result)
      }
    } finally {
      isSubmittingRef.current = false
      if (!isPublishing && !isCreating) {
        setIsLocalSubmitting(false)
      }
    }
  }, [methods, createDraftService, onClose, onSuccess, resetDialogState, isCreating, isPublishing])

  const handleClose = () => {
    resetDialogState()
    onClose()
  }

  const publishButtonLabel = useMemo(() => {
    if (isCreating) return T.PUBLISHING
    if (isScheduleTab) return T.PUBLISH_AND_ROUTE
    return T.PUBLISH_NOW
  }, [isCreating, isScheduleTab])

  const dialogContent = (
    <>
      <HHDialogTitle title={T.NEW_SERVICE} onClose={handleClose} />
      <DialogContent
        sx={{
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
        }}
      >
        <Box sx={{ display: isScheduleTab ? 'block' : 'none', flex: 1, minHeight: 0 }}>
          <ScheduleTabDraft metaFiltersData={metaFiltersData} onRegisterFiltersReset={handleRegisterFiltersReset} />
        </Box>
        <Box sx={{ display: isCreateTab ? 'block' : 'none', flex: 1, minHeight: 0 }}>
          <CreateDraftServiceForm />
        </Box>
      </DialogContent>
      <HHDialogActions
        sx={{
          justifyContent: { xs: 'center', sm: 'space-between' },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1,
          pr: { xs: 2, sm: '84px' },
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <CancelButton size="small" onClick={handleClose} />
          {showBackToServices && (
            <Button size="small" variant="outlined" color="primary" onClick={handleBackToServicesClick} startIcon={<ArrowBackIcon />} sx={{ whiteSpace: 'nowrap' }}>
              {T.BACK_TO_SERVICES}
            </Button>
          )}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {!isScheduleTab && (
            <Button size="small" variant="outlined" onClick={handleSaveDraftClick} disabled={isCreateDraftDisabled} loading={isCreating} sx={{ whiteSpace: 'nowrap' }}>
              {isCreating ? T.CREATING_DRAFT : T.CREATE_DRAFT}
            </Button>
          )}
          {showScheduleServiceDay && (
            <Button size="small" variant="contained" color="primary" disabled={isScheduleButtonDisabled} onClick={handleGoToScheduleClick} sx={{ whiteSpace: 'nowrap' }}>
              {T.SCHEDULE_SERVICE_DAY}
            </Button>
          )}
          {isScheduleTab && (
            <Button
              size="small"
              variant="outlined"
              onClick={handlePublishNowClick}
              disabled={isActionDisabled}
              loading={isCreatingOrPublishing}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {T.PUBLISH_NOW}
            </Button>
          )}
          {showPublish && (
            <Button
              size="small"
              variant="outlined"
              onClick={handlePublishClick}
              disabled={isActionDisabled}
              loading={isCreatingOrPublishing}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {publishButtonLabel}
            </Button>
          )}
          {showPublish && onSuccessReturnToAccount && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handlePublishAndReturnToAccountClick}
              disabled={isActionDisabled}
              loading={isCreatingOrPublishing}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {T.PUBLISH_AND_RETURN_TO_ACCOUNT}
            </Button>
          )}
        </Stack>
      </HHDialogActions>
    </>
  )

  if (renderAsPanel && isDesktop) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          borderLeft: 1,
          borderColor: 'divider',
        }}
      >
        {dialogContent}
      </Box>
    )
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      fullScreen
      sx={{
        zIndex: Z_INDEX.NAVIGATION,
      }}
    >
      {dialogContent}
    </Dialog>
  )
}

const CreateDraftConfiguredServiceDialog = props => (
  <CreateDraftConfiguredServiceProvider {...props}>
    <CreateDraftConfiguredServiceDialogContent />
  </CreateDraftConfiguredServiceProvider>
)

CreateDraftConfiguredServiceDialog.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  onSuccessReturnToAccount: PropTypes.func,
  accountId: PropTypes.string.isRequired,
  locationId: PropTypes.string,
  renderAsPanel: PropTypes.bool,
}

export default CreateDraftConfiguredServiceDialog
